import type { FC } from "react";

import type { MenuKit } from "../files/menuKit";
import { canDropInto } from "../files/treeRows";
import type { WsNode } from "./types";
import { canRenameRow } from "./WorkspaceRow";
import type { WsRow } from "./workspaceModel";

/**
 * Work Products actions shared by the right-click menu and the row's
 * three-dot menu. A row's menu acts on that row (and a multi-selection it
 * belongs to). The empty-area menu only creates and refreshes. Neither
 * offers Select All.
 */
export type WorkProductsMenuModel = {
  menuRow: WsRow | null;
  menuIds: string[];
  menuNodes: WsNode[];
  folders: WsNode[];
  nodes: WsNode[];
  paths: Map<string, string>;
  menuInForms: boolean;
  menuFolderId: string | null;
  menuHasPacket: boolean;
  canCopyToCaseFiles: boolean;
  rootName: string;
};

export type WorkProductsMenuActions = {
  openRow: (row: WsRow) => void;
  mention: (row: WsRow) => void;
  createFolder: (parentId: string | null) => void;
  addForm: (parentId: string | null) => void;
  createDraft: (parentId: string | null) => void;
  rename: (id: string) => void;
  move: (ids: string[], parentId: string | null) => void;
  addToCaseFiles: () => void;
  deleteNodes: (ids: string[]) => void;
  refresh: () => void;
};

export const WorkProductsMenuItems: FC<{
  kit: MenuKit;
  model: WorkProductsMenuModel;
  actions: WorkProductsMenuActions;
}> = ({ kit, model, actions }) => {
  const { Item, Separator, Sub, SubTrigger, SubContent } = kit;
  const {
    menuRow,
    menuIds,
    menuNodes,
    folders,
    nodes,
    paths,
    menuInForms,
    menuFolderId,
    menuHasPacket,
    canCopyToCaseFiles,
    rootName
  } = model;

  if (!menuRow) {
    return (
      <>
        <Item onSelect={() => actions.createFolder(null)}>New folder</Item>
        <Item onSelect={() => actions.createDraft(null)}>New draft</Item>
        <Separator />
        <Item onSelect={actions.refresh}>Refresh</Item>
      </>
    );
  }

  return (
    <>
      <Item onSelect={() => actions.openRow(menuRow)}>
        {menuRow.node.kind === "folder"
          ? menuRow.expanded
            ? "Collapse"
            : "Expand"
          : "Open"}
      </Item>
      {menuRow.item ? (
        <Item onSelect={() => actions.mention(menuRow)}>Mention in chat</Item>
      ) : null}
      <Separator />
      {menuRow.node.kind === "folder" ? (
        <Item onSelect={() => actions.createFolder(menuRow.node.id)}>
          New folder inside
        </Item>
      ) : null}
      {menuInForms ? (
        <Item onSelect={() => actions.addForm(menuFolderId)}>Add Form…</Item>
      ) : (
        <Item onSelect={() => actions.createDraft(menuFolderId)}>
          New draft here
        </Item>
      )}
      <Item
        disabled={menuIds.length !== 1 || !canRenameRow(menuRow)}
        onSelect={() => actions.rename(menuRow.node.id)}
      >
        Rename
      </Item>
      <Sub>
        <SubTrigger>Move to</SubTrigger>
        <SubContent className="max-h-72 w-60 overflow-y-auto">
          <Item
            disabled={
              !canDropInto(nodes, menuIds, null) ||
              menuNodes.every((n) => n.parent_id === null)
            }
            onSelect={() => actions.move(menuIds, null)}
          >
            {rootName}
          </Item>
          {folders
            .filter(
              (f) =>
                canDropInto(nodes, menuIds, f.id) &&
                !menuNodes.every((n) => n.parent_id === f.id)
            )
            .map((f) => (
              <Item key={f.id} onSelect={() => actions.move(menuIds, f.id)}>
                <span className="truncate">{paths.get(f.id) || f.name}</span>
              </Item>
            ))}
        </SubContent>
      </Sub>
      {canCopyToCaseFiles ? (
        <Item onSelect={actions.addToCaseFiles}>Add to Case Files…</Item>
      ) : null}
      <Separator />
      <Item
        variant="destructive"
        disabled={menuHasPacket}
        onSelect={() => actions.deleteNodes(menuIds)}
      >
        Delete
        {menuIds.length > 1 ? ` ${menuIds.length} items` : ""}
      </Item>
      {menuHasPacket ? (
        <div className="px-2 pb-1 text-xs text-muted-foreground">
          The filing packet cannot be deleted; move it out first.
        </div>
      ) : null}
    </>
  );
};
